import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, MessageCircle, PhoneOutgoing, ShieldCheck, Clock, MapPin, Award, CheckCircle2, Gift, ArrowRight, FileCheck2, CarFront, CloudHail, Shield, Umbrella, FileSearch, Truck, Calculator, TrendingUp, Star, Zap } from "lucide-react";
import { Seo, faqSchema } from "../components/Seo";
import { Reveal, SectionLabel, CtaBlock, TuvMark } from "../components/Blocks";
import { SofortHilfeBox } from "../components/SofortHilfe";
import { RueckrufForm } from "../components/RueckrufForm";
import { ReviewsBlock, HeroRatingBadge } from "../components/ReviewsBlock";
import { FallbeispieleBlock } from "../components/FallbeispieleBlock";
import { openRueckrufModal } from "../components/Layout";
import { Faq } from "../components/Faq";
import { SITE, IMAGES, CITY_LINKS, PROMISES } from "../data/site";
import { cities as ALL_CITIES } from "../data/cities";
import { services } from "../data/services";
import { API } from "@/lib/api";
import { ratgeber } from "../data/ratgeber";

const ICONS = { FileCheck2, CarFront, ShieldCheck, CloudHail, Shield, Umbrella, FileSearch, Truck, Calculator, TrendingUp };

const USPS = ["100% kostenlos für Geschädigte", "Vor-Ort-Service", "TÜV-zertifiziert", PROMISES.lieferzeitKurz, "Versicherungsunabhängig", "Ganz NRW"];

// Auf der Startseite nur echte Vor-Ort-Standorte zeigen – das bundesweite
// Partnernetzwerk (Hamburg, Berlin …) würde von Köln/NRW ablenken.
const VOR_ORT_SLUGS = new Set(ALL_CITIES.filter((c) => c.vor_ort !== false).map((c) => c.slug));
const VOR_ORT_CITY_LINKS = CITY_LINKS.filter((c) => VOR_ORT_SLUGS.has(c.slug));

const HOME_FAQS = [
  { q: "Was kostet mich das Gutachten als Unfallgeschädigter?", a: "Nichts. Bei einem unverschuldeten Unfall übernimmt die gegnerische Haftpflichtversicherung die Gutachterkosten vollständig – das ist Ihr gesetzliches Recht." },
  { q: "Wie schnell bekomme ich mein Gutachten?", a: "Die Begutachtung vor Ort dauert ca. 30–40 Minuten. Das fertige Gutachten erhalten Sie in der Regel innerhalb von 24 Stunden." },
  { q: "Muss ich den Gutachter der gegnerischen Versicherung akzeptieren?", a: "Nein. Sie haben das Recht auf einen freien, unabhängigen Sachverständigen Ihrer Wahl. Nutzen Sie dieses Recht – es schützt Sie vor Kürzungen." },
  { q: "Wo findet die Begutachtung statt?", a: "Dort, wo es Ihnen passt: zu Hause, am Arbeitsplatz, in Ihrer Werkstatt oder am Standort des Fahrzeugs – täglich zwischen 09:30 und 19:30 Uhr." },
  { q: "Kann ich mir die Reparaturkosten auszahlen lassen?", a: "Ja. Alternativ zur Reparatur können Sie sich die im Gutachten ermittelten Kosten auszahlen lassen (fiktive Abrechnung). Wir beraten Sie, was sich für Sie lohnt." },
];

const Hero = () => {
  const { scrollY } = useScroll();
  const yGlow = useTransform(scrollY, [0, 600], [0, 120]);
  return (
    <section className="ci-gradient grain relative overflow-hidden text-white" data-testid="hero-section">
      <motion.div style={{ y: yGlow }} className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full bg-ember/25 blur-[120px] pointer-events-none" />
      <div className="absolute top-1/2 -left-40 w-[420px] h-[420px] rounded-full bg-royal/40 blur-[110px] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 lg:py-20 grid lg:grid-cols-[1.2fr_1fr] gap-12 items-center relative z-10">
        <div>
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/15 rounded-full px-4 py-1.5 text-xs font-semibold tracking-wide mb-6" data-testid="hero-tuev-badge">
            <Award size={14} className="text-ember" /> TÜV-zertifizierte KFZ-Sachverständige – in ganz NRW für Sie vor Ort
          </div>
          <HeroRatingBadge />
          <h1 className="mt-5 font-display font-bold uppercase leading-[0.95] tracking-tight text-5xl sm:text-6xl lg:text-7xl">
            <span className="reveal-line"><span>Ihr Kfz-Gutachter</span></span>
            <span className="reveal-line"><span>in ganz NRW –</span></span>
            <span className="reveal-line"><span className="ci-gradient-text">kostenlos für Geschädigte.</span></span>
          </h1>
          <p className="mt-6 text-slate-200 text-base sm:text-lg max-w-xl leading-relaxed">
            Sie hatten einen unverschuldeten Unfall? Dann zahlt die gegnerische Versicherung Ihr Gutachten. Wir kommen zu Ihnen vor Ort – in Köln, Krefeld und ganz NRW. {PROMISES.lieferzeit}.
          </p>
          <a href={SITE.phoneHref} data-testid="hero-phone-call-button" className="mt-8 inline-flex items-center gap-4 group">
            <span className="cta-gradient p-4 rounded-2xl"><Phone size={26} /></span>
            <span>
              <span className="block text-xs uppercase tracking-[0.2em] text-slate-300">Mo–Fr 9–22 Uhr erreichbar</span>
              <span className="font-display text-3xl sm:text-4xl font-bold group-hover:text-ember transition-colors">{SITE.phone}</span>
            </span>
          </a>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <button onClick={openRueckrufModal} data-testid="hero-callback-button" className="cta-gradient inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-bold text-white">
              <PhoneOutgoing size={18} /> Kostenlosen Rückruf anfordern
            </button>
            <a href={`https://wa.me/${SITE.whatsapp}?text=${encodeURIComponent(SITE.whatsappText)}`} target="_blank" rel="noopener noreferrer" data-testid="hero-whatsapp-button" className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold border border-white/30 hover:bg-white/10 transition-colors">
              <MessageCircle size={17} /> WhatsApp
            </a>
          </div>
          <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-300">
            {["Kostenlos für Geschädigte", PROMISES.termin, "Unabhängig & TÜV"].map((t) => (
              <span key={t} className="flex items-center gap-1.5"><CheckCircle2 size={15} className="text-ember" /> {t}</span>
            ))}
          </div>
          <div className="mt-7 inline-flex items-center gap-4 bg-white rounded-2xl px-5 py-3.5 shadow-xl" data-testid="hero-tuv-mark-card">
            <img src="/tuv-pruefzeichen-440.webp" alt="TÜV Rheinland Prüfzeichen – Geprüfte Qualifikation, gültig bis 15.11.2026" width="440" height="162" className="h-14 w-auto" />
            <span className="text-navy">
              <span className="block font-display font-bold text-sm leading-tight">TÜV Rheinland geprüft</span>
              <span className="block text-[11px] text-slate-500 mt-0.5 leading-snug">Prüf-ID 0000046955 · gültig bis 15.11.2026</span>
            </span>
          </div>
        </div>
        <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}>
          <RueckrufForm source="hero" testid="hero-form" heading="Unfall gehabt? Wir rufen zurück." subline="Kurz eintragen – wir rufen Sie zurück. Kostenlos & unverbindlich." />
        </motion.div>
      </div>
    </section>
  );
};

const HeroImageBand = () => (
  <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20" data-testid="hero-image-band">
    <Reveal>
      <div className="relative rounded-3xl overflow-hidden shadow-2xl">
        <img src="/hero-gutachter.webp" srcSet="/hero-gutachter-640.webp 640w, /hero-gutachter-960.webp 960w, /hero-gutachter.webp 1376w" sizes="(min-width: 1280px) 1216px, calc(100vw - 32px)" width="1376" height="768" alt="Gutachter von UnfallExpert24 schüttelt einer erleichterten Kundin nach dem Unfall die Hand – wir helfen Ihnen" className="w-full h-[320px] sm:h-[460px] object-cover" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy/85 via-navy/20 to-transparent" />
        <div className="absolute bottom-0 left-0 p-6 sm:p-10 text-white max-w-xl">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-ember">Echt. Vor Ort. Unabhängig.</p>
          <p className="font-display text-2xl sm:text-4xl font-bold mt-2 leading-tight">Wir helfen Ihnen – persönlich, direkt vor Ort in ganz NRW</p>
        </div>
        <div className="absolute top-3 right-3 sm:top-6 sm:right-6">
          <img src="/tuv-pruefzeichen-440.webp" alt="TÜV Rheinland Prüfzeichen – Geprüfte Qualifikation" width="440" height="162" className="sm:hidden h-11 w-auto bg-white rounded-lg p-1 shadow-lg" />
          <div className="hidden sm:block sm:w-72">
            <TuvMark compact />
          </div>
        </div>
      </div>
    </Reveal>
  </section>
);

const UspMarquee = () => (
  <div className="bg-white border-b border-slate-200 overflow-hidden py-4" data-testid="usp-marquee" aria-label="Unsere Vorteile">
    <div className="flex whitespace-nowrap animate-marquee w-max">
      {[0, 1].map((dup) => (
        <div key={dup} className="flex items-center" aria-hidden={dup === 1}>
          {USPS.concat(USPS).map((u, i) => (
            <span key={`${dup}-${i}`} className="flex items-center gap-3 px-6 text-sm font-semibold text-navy">
              <Zap size={15} className="text-ember" /> {u}
            </span>
          ))}
        </div>
      ))}
    </div>
  </div>
);

const TeamTrust = () => (
  <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-14 sm:pb-20" data-testid="team-trust-section">
    <div className="grid lg:grid-cols-2 gap-10 items-center">
      <Reveal>
        <div className="relative rounded-3xl overflow-hidden shadow-2xl">
          <img src="/team/team-gruppe.webp" alt="Das Team von UnfallExpert24: Denis Sieger, Giovanni Timperio und Sezgin Cömlek – Inhaber des TÜV-Prüfzeichens KFZ-Sachverständiger für Schäden und Bewertung" className="w-full h-[340px] sm:h-[420px] object-cover object-top" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy/70 via-transparent to-transparent" />
          <div className="absolute bottom-0 p-6 text-white">
            <p className="font-display text-xl sm:text-2xl font-bold">Denis · Giovanni · Sezgin</p>
            <p className="text-sm text-slate-200">Ihre TÜV-zertifizierten Gutachter in Köln & Krefeld</p>
          </div>
        </div>
      </Reveal>
      <Reveal delay={0.15}>
        <SectionLabel>Ihr Team vor Ort</SectionLabel>
        <h2 className="font-display text-4xl sm:text-5xl font-bold text-navy mt-3">Echte Menschen statt Callcenter</h2>
        <p className="text-slate-600 mt-4 leading-relaxed">Bei uns sprechen Sie direkt mit den Gutachtern, die auch zu Ihnen kommen: Denis Sieger, Giovanni Timperio und Sezgin Cömlek – Inhaber des TÜV-Prüfzeichens „KFZ-Sachverständiger für Schäden und Bewertung". Persönlich, erreichbar und auf Ihrer Seite.</p>
        <ul className="mt-5 space-y-2.5">
          {["Direkter Draht zu Ihrem Gutachter – keine Warteschleife", "Inhaber des TÜV-Prüfzeichens (ID 0000046955)", "Termine täglich 09:30–19:30 Uhr, auch samstags"].map((v) => (
            <li key={v} className="flex items-start gap-2 text-sm text-slate-700"><CheckCircle2 size={16} className="text-ember shrink-0 mt-0.5" /> {v}</li>
          ))}
        </ul>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link to="/ueber-uns/" data-testid="team-trust-about-button" className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold border border-slate-300 text-navy hover:border-ember hover:text-ember transition-colors">Team kennenlernen <ArrowRight size={17} /></Link>
          <a href={SITE.phoneHref} className="cta-gradient inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold text-white"><Phone size={17} /> Direkt anrufen</a>
        </div>
      </Reveal>
    </div>
  </section>
);

const Steps = () => (
  <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24" data-testid="steps-section">
    <Reveal>
      <SectionLabel>So einfach geht's</SectionLabel>
      <h2 className="font-display text-4xl sm:text-5xl font-bold text-navy mt-3 max-w-2xl">In 3 Schritten zu Ihrem Gutachten</h2>
    </Reveal>
    <div className="grid md:grid-cols-3 gap-6 mt-10">
      {[
        { n: "01", t: "Schaden melden", d: "Rufen Sie uns an oder melden Sie den Schaden online – dauert keine zwei Minuten. Wir beraten Sie sofort und kostenlos.", icon: Phone },
        { n: "02", t: "Gutachter kommt zu Ihnen", d: "Wir vereinbaren einen Termin direkt bei Ihnen vor Ort. Die Begutachtung dauert nur 30–40 Minuten – auch abends bis 19:30 Uhr.", icon: MapPin },
        { n: "03", t: "Gutachten & Auszahlung", d: "Innerhalb von 24 Stunden ist Ihr Gutachten fertig. Damit reguliert die Versicherung – Reparatur oder Auszahlung, Sie entscheiden.", icon: FileCheck2 },
      ].map((s, i) => (
        <Reveal key={s.n} delay={i * 0.12}>
          <div className="card-hover bg-white rounded-2xl border border-slate-200 p-7 h-full relative overflow-hidden">
            <span className="absolute -top-3 right-4 font-display text-7xl font-bold text-slate-100 select-none">{s.n}</span>
            <s.icon size={28} className="text-ember" />
            <h3 className="font-display text-2xl font-bold text-navy mt-4">{s.t}</h3>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">{s.d}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

const ServicesGrid = () => (
  <section className="bg-navy text-white relative grain" data-testid="services-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24 relative z-10">
      <Reveal className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <SectionLabel>Leistungen</SectionLabel>
          <h2 className="font-display text-4xl sm:text-5xl font-bold mt-3">Vom Unfallgutachten bis zur Wertbestimmung</h2>
        </div>
        <Link to="/leistungen/" data-testid="services-all-link" className="inline-flex items-center gap-2 text-sm font-bold text-ember hover:text-white transition-colors">Alle Leistungen <ArrowRight size={16} /></Link>
      </Reveal>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
        {services.map((s, i) => {
          const Icon = ICONS[s.icon] || FileCheck2;
          return (
            <Reveal key={s.slug} delay={(i % 3) * 0.1}>
              <Link to={`/${s.slug}`} data-testid={`service-card-${s.slug}`} className="card-hover block bg-white/5 border border-white/10 rounded-2xl p-6 h-full backdrop-blur-sm group">
                <Icon size={26} className="text-ember" />
                <h3 className="font-display text-xl font-bold mt-3 group-hover:text-ember transition-colors">{s.title}</h3>
                <p className="text-sm text-slate-300 mt-1.5 leading-relaxed">{s.short}</p>
                <span className="inline-flex items-center gap-1.5 text-xs font-bold text-ember mt-4">Mehr erfahren <ArrowRight size={13} /></span>
              </Link>
            </Reveal>
          );
        })}
        <Reveal delay={0.2}>
          <Link to="/schaden-melden-formular" className="cta-gradient card-hover rounded-2xl p-6 h-full flex flex-col justify-between min-h-[180px]" data-testid="service-card-cta">
            <h3 className="font-display text-2xl font-bold">Unfall gehabt?<br />Jetzt Schaden melden.</h3>
            <span className="inline-flex items-center gap-2 font-bold">Zum Formular <ArrowRight size={18} /></span>
          </Link>
        </Reveal>
      </div>
    </div>
  </section>
);

const WhyUs = () => (
  <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24" data-testid="why-section">
    <Reveal>
      <SectionLabel>Warum UnfallExpert24</SectionLabel>
      <h2 className="font-display text-4xl sm:text-5xl font-bold text-navy mt-3 max-w-2xl">Vier Gründe, warum Geschädigte uns vertrauen</h2>
    </Reveal>
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
      <Reveal className="lg:row-span-2">
        <div className="relative rounded-2xl overflow-hidden h-full min-h-[320px]">
          <img src={IMAGES.appraiserInspection} alt="TÜV-zertifizierter KFZ-Sachverständiger begutachtet einen Unfallschaden am Fahrzeug" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/40 to-transparent" />
          <div className="absolute bottom-0 p-6 text-white">
            <p className="font-display text-2xl font-bold">TÜV-zertifiziert & versicherungsunabhängig</p>
            <p className="text-sm text-slate-200 mt-1">Wir arbeiten ausschließlich in Ihrem Interesse – nicht für Versicherungen.</p>
          </div>
        </div>
      </Reveal>
      {[
        { icon: MapPin, t: "Kostenfreier Vor-Ort-Service", d: "Wir kommen zu Ihnen, wann es Ihnen passt – auch in der Mittagspause oder nach Feierabend. 100 % kostenlos für Unfallgeschädigte." },
        { icon: Clock, t: "Gutachten in 24 Stunden", d: "Damit der Schaden zügig reguliert werden kann: Innerhalb von 24 Stunden ist Ihr Gutachten fertig und kann bei der Versicherung eingereicht werden." },
        { icon: ShieldCheck, t: "Schnelle Hilfe nach dem Unfall", d: "Sie wissen nicht, was zu tun ist? Bewahren Sie Ruhe – unser Team sagt Ihnen genau, welche Schritte jetzt wichtig sind." },
        { icon: Star, t: "Auszahlung der Reparaturkosten", d: "Reparatur oder Barauszahlung: Sobald die Versicherung reguliert, entscheiden Sie frei, was mit Ihrem Fahrzeug passiert." },
      ].map((v, i) => (
        <Reveal key={v.t} delay={i * 0.1}>
          <div className="card-hover bg-white rounded-2xl border border-slate-200 p-6 h-full">
            <v.icon size={26} className="text-ember" />
            <h3 className="font-display text-xl font-bold text-navy mt-3">{v.t}</h3>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">{v.d}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

const Tankgutschein = () => (
  <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-16 sm:pb-24" data-testid="tankgutschein-section">
    <Reveal>
      <div className="cta-gradient rounded-3xl p-8 sm:p-12 text-white relative overflow-hidden">
        <Gift size={180} className="absolute -right-8 -bottom-10 text-white/10 rotate-12" />
        <div className="relative z-10 max-w-2xl">
          <span className="text-xs font-bold uppercase tracking-[0.25em]">Aktion</span>
          <h2 className="font-display text-4xl sm:text-5xl font-bold mt-2">50 € Tankgutschein gratis</h2>
          <p className="mt-3 text-white/90 leading-relaxed">Für Geschädigte und Empfehlende: Melden Sie Ihren Schaden über uns oder empfehlen Sie uns weiter – und sichern Sie sich einen 50-Euro-Tankgutschein.</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/schaden-melden-formular" data-testid="tankgutschein-claim-button" className="inline-flex items-center gap-2 bg-white text-signal font-bold px-7 py-3.5 rounded-full hover:bg-slate-100 transition-colors">
              Jetzt Schaden melden <ArrowRight size={18} />
            </Link>
            <Link to="/freunde-empfehlen" data-testid="tankgutschein-referral-button" className="inline-flex items-center gap-2 border border-white/40 text-white font-bold px-7 py-3.5 rounded-full hover:bg-white/10 transition-colors">
              Freund empfehlen <Gift size={18} />
            </Link>
          </div>
        </div>
      </div>
    </Reveal>
  </section>
);

const RatgeberTeaser = () => {
  const [articles, setArticles] = useState([]);
  useEffect(() => {
    axios.get(`${API}/articles`).then((r) => setArticles(r.data.slice(0, 3))).catch(() => {});
  }, []);
  const items = articles.length
    ? articles.map((a) => ({ slug: a.slug, path: `/ratgeber/${a.slug}/`, title: a.title, excerpt: a.teaser, tag: "Magazin" }))
    : ratgeber.slice(0, 3).map((r) => ({ ...r, tag: "Ratgeber" }));
  return (
  <section className="bg-white border-y border-slate-200" data-testid="ratgeber-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
      <Reveal className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <SectionLabel>Aktuelle Ratgeber</SectionLabel>
          <h2 className="font-display text-4xl sm:text-5xl font-bold text-navy mt-3">Gut zu wissen nach dem Unfall</h2>
        </div>
        <Link to="/ratgeber/" data-testid="home-alle-ratgeber-button" className="inline-flex items-center gap-2 text-sm font-bold text-ember hover:text-signal transition-colors">Alle Ratgeber ansehen <ArrowRight size={16} /></Link>
      </Reveal>
      <div className="grid md:grid-cols-3 gap-5 mt-10">
        {items.map((r, i) => (
          <Reveal key={r.slug} delay={i * 0.1}>
            <Link to={r.path} data-testid={`ratgeber-teaser-${r.slug}`} className="card-hover block bg-slate-50 border border-slate-200 rounded-2xl p-6 h-full group">
              <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-ember">{r.tag}</span>
              <h3 className="font-display text-xl font-bold text-navy mt-2 group-hover:text-ember transition-colors">{r.title}</h3>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed line-clamp-3">{r.excerpt}</p>
              <span className="inline-flex items-center gap-1.5 text-xs font-bold text-royal mt-4">Weiterlesen <ArrowRight size={13} /></span>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
  );
};

const Areas = () => (
  <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24" data-testid="areas-section">
    <div className="grid lg:grid-cols-2 gap-10 items-center">
      <Reveal>
        <SectionLabel>Einsatzgebiete</SectionLabel>
        <h2 className="font-display text-4xl sm:text-5xl font-bold text-navy mt-3">Vor Ort in ganz NRW</h2>
        <p className="text-slate-600 mt-4 leading-relaxed">Mit Standorten in Köln und Krefeld sind unsere Gutachter in ganz Nordrhein-Westfalen unterwegs – vom Ruhrgebiet über das Rheinland bis an den Niederrhein. Unsere Hauptstandorte:</p>
        <div className="flex flex-wrap gap-2.5 mt-5">
          {[["koeln", "Köln"], ["krefeld", "Krefeld"], ["moers", "Moers"], ["duisburg", "Duisburg"], ["essen", "Essen"]].map(([slug, name]) => (
            <Link key={slug} to={`/kfz-gutachter-${slug}`} data-testid={`areas-main-link-${slug}`} className="px-4 py-2 rounded-full bg-navy text-white text-sm font-bold hover:bg-ember transition-colors">Kfz-Gutachter {name}</Link>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 mt-6">
          {VOR_ORT_CITY_LINKS.map((c) => (
            <Link key={c.slug} to={`/kfz-gutachter-${c.slug}`} data-testid={`city-chip-${c.slug}`} className="px-3.5 py-1.5 rounded-full bg-white border border-slate-200 text-sm font-semibold text-navy hover:border-ember hover:text-ember transition-colors">
              {c.name}
            </Link>
          ))}
        </div>
        <Link to="/standorte" className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-ember">Alle Standorte im Detail <ArrowRight size={16} /></Link>
      </Reveal>
      <Reveal delay={0.15}>
        <div className="relative rounded-3xl overflow-hidden shadow-2xl">
          <img src={IMAGES.cologne} alt="Köln mit Dom und Rhein – Heimatstandort von UnfallExpert24" className="w-full h-[380px] object-cover" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy/80 via-transparent to-transparent" />
          <div className="absolute bottom-0 p-6 text-white">
            <p className="font-display text-2xl font-bold">Köln · Krefeld · NRW</p>
            <p className="text-sm text-slate-200">{PROMISES.erreichbarkeit}</p>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

const HOME_JSONLD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "AutomotiveBusiness",
      name: "UnfallExpert24 – Ing. & KFZ-Sachverständigenbüro",
      description: "Unabhängige, TÜV-zertifizierte KFZ-Sachverständige für Unfallgutachten und Fahrzeugbewertung. Vor-Ort-Service in ganz NRW.",
      url: SITE.domain,
      telephone: "+4922167029067",
      priceRange: "Kostenlos für Unfallgeschädigte",
      address: { "@type": "PostalAddress", streetAddress: "Vogelsanger Straße 11", postalCode: "50823", addressLocality: "Köln", addressRegion: "Nordrhein-Westfalen", addressCountry: "DE" },
      openingHoursSpecification: [
        { "@type": "OpeningHoursSpecification", dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"], opens: "09:00", closes: "22:00" },
      ],
      areaServed: ["Köln", "Krefeld", "Moers", "Duisburg", "Essen", "Nordrhein-Westfalen"].map((n) => ({ "@type": "Place", name: n })),
      sameAs: [SITE.facebook, SITE.instagram, SITE.youtube, SITE.linkedin, SITE.tiktok],
    },
    faqSchema(HOME_FAQS),
  ],
};

const Home = () => (
  <>
    <Seo
      title="UnfallExpert24 – Kfz-Gutachter NRW | Unfallgutachten kostenlos für Geschädigte"
      description="TÜV-zertifizierte Kfz-Sachverständige in ganz NRW ✓ Unfallgutachten kostenlos für Geschädigte ✓ Wir kommen zu Ihnen ✓ Abwicklung mit der Versicherung. ☎ 0221 67029067"
      path="/"
      jsonLd={HOME_JSONLD}
    />
    <Hero />
    <ReviewsBlock />
    <SofortHilfeBox />
    <UspMarquee />
    <HeroImageBand />
    <Steps />
    <ServicesGrid />
    <WhyUs />
    <section className="max-w-4xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
      <Reveal><Faq faqs={HOME_FAQS} /></Reveal>
    </section>
    <TeamTrust />
    <FallbeispieleBlock />
    <Tankgutschein />
    <RatgeberTeaser />
    <Areas />
    <section className="ci-gradient grain py-16 sm:py-20" data-testid="home-second-form">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-10 items-center">
        <Reveal className="text-white">
          <h2 className="font-display text-3xl sm:text-4xl font-bold">Ein Anruf genügt – oder wir rufen Sie zurück</h2>
          <p className="mt-4 text-slate-200 leading-relaxed">Schildern Sie uns kurz Ihren Fall. {PROMISES.rueckruf} – und bei unverschuldetem Unfall ist das Gutachten für Sie kostenlos.</p>
          <a href={SITE.phoneHref} data-testid="home-second-call-button" className="mt-6 inline-flex items-center gap-3 font-display text-2xl font-bold hover:text-ember transition-colors"><Phone size={24} /> {SITE.phone}</a>
        </Reveal>
        <Reveal delay={0.15}><RueckrufForm source="home-zweites-formular" testid="home-second-form-inner" /></Reveal>
      </div>
    </section>
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
      <CtaBlock />
    </section>
  </>
);

export default Home;
